import { useLocation, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { Home, Dumbbell, Grid, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SearchBar } from "@/components/SearchBar";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(true);

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
    // Signal "this is a 404" to prerenderers / SEO tools that look at this meta.
    // Modern Googlebot still indexes 404s as "Not Found" without it, but adding it
    // helps third-party crawlers (Bing, Yandex, AI bots) classify the page correctly.
    let statusMeta = document.querySelector('meta[name="prerender-status-code"]');
    if (!statusMeta) {
      statusMeta = document.createElement("meta");
      statusMeta.setAttribute("name", "prerender-status-code");
      document.head.appendChild(statusMeta);
    }
    statusMeta.setAttribute("content", "404");
    fetchCategories();
    findClosestMatch();
    return () => {
      const meta = document.querySelector('meta[name="prerender-status-code"]');
      if (meta) meta.remove();
    };
  }, [location.pathname]);

  const extractKeywords = (path: string): string[] => {
    return path
      .split("/")
      .filter(Boolean)
      .flatMap(segment => segment.split(/[-_]/))
      .filter(word => word.length > 2)
      .map(word => word.toLowerCase());
  };

  const calculateMatchScore = (item: any, keywords: string[]): number => {
    let score = 0;
    const searchableText = `${item.name || item.title || item.full_name || ''} ${item.slug || ''} ${item.description || item.summary || item.bio || ''}`.toLowerCase();
    
    keywords.forEach(keyword => {
      const lowerKeyword = keyword.toLowerCase();
      // Exact slug match = highest score
      if ((item.slug || '').toLowerCase() === lowerKeyword) {
        score += 100;
      }
      // Title/name exact match
      else if ((item.name || item.title || item.full_name || '').toLowerCase() === lowerKeyword) {
        score += 50;
      }
      // Slug contains keyword
      else if ((item.slug || '').toLowerCase().includes(lowerKeyword)) {
        score += 30;
      }
      // Title/name contains keyword
      else if ((item.name || item.title || item.full_name || '').toLowerCase().includes(lowerKeyword)) {
        score += 20;
      }
      // Description contains keyword
      else if (searchableText.includes(lowerKeyword)) {
        score += 5;
      }
    });
    
    return score;
  };

  const findClosestMatch = async () => {
    setIsSearching(true);
    const keywords = extractKeywords(location.pathname);
    
    if (keywords.length === 0) {
      setIsSearching(false);
      return;
    }

    try {
      const searchTerm = keywords.join(" ");
      const matches: any[] = [];

      // Search listings
      const { data: listings } = await supabase
        .from("listings")
        .select("id, name, slug, description, short_description")
        .or(`name.ilike.%${searchTerm}%,slug.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,short_description.ilike.%${searchTerm}%`)
        .limit(10);

      if (listings) {
        matches.push(...listings.map(item => ({ ...item, type: "listing", path: `/listing/${item.slug}`, title: item.name })));
      }

      // Search articles
      const { data: articles } = await supabase
        .from("articles")
        .select("id, title, slug, summary")
        .or(`title.ilike.%${searchTerm}%,slug.ilike.%${searchTerm}%,summary.ilike.%${searchTerm}%`)
        .limit(10);

      if (articles) {
        matches.push(...articles.map(item => ({ ...item, type: "article", path: `/article/${item.slug}` })));
      }

      // Search trainers
      const { data: trainers } = await supabase
        .from("trainer_profiles")
        .select("id, full_name, slug, bio")
        .or(`full_name.ilike.%${searchTerm}%,slug.ilike.%${searchTerm}%,bio.ilike.%${searchTerm}%`)
        .limit(10);

      if (trainers) {
        matches.push(...trainers.map(item => ({ ...item, type: "trainer", path: `/trainer/${item.slug}`, title: item.full_name })));
      }

      // Search events
      const { data: events } = await supabase
        .from("events")
        .select("id, title, slug, description")
        .or(`title.ilike.%${searchTerm}%,slug.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
        .limit(10);

      if (events) {
        matches.push(...events.map(item => ({ ...item, type: "event", path: `/event/${item.slug}` })));
      }

      if (matches.length > 0) {
        // Score all matches
        const scoredMatches = matches.map(match => ({
          ...match,
          score: calculateMatchScore(match, keywords)
        }));

        // Sort by score descending
        scoredMatches.sort((a, b) => b.score - a.score);

        // Get the best match
        const bestMatch = scoredMatches[0];

        // Auto-redirect to best match if score is significant
        if (bestMatch.score > 0) {
          toast.success("Redirigiendo a la página correcta...");
          setTimeout(() => navigate(bestMatch.path), 800);
          return;
        }
      }
      
      // No good matches found
      setSuggestions([]);
    } catch (error) {
      console.error("Error finding closest match:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name", { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Define common listing types for quick access
  const popularTypes = [
    { name: "Gimnasios", slug: "gym", icon: "🏋️" },
    { name: "Yoga", slug: "yoga", icon: "🧘" },
    { name: "CrossFit", slug: "crossfit", icon: "💪" },
    { name: "Artes Marciales", slug: "martial_arts", icon: "🥋" },
    { name: "Pilates", slug: "pilates", icon: "🤸" },
    { name: "Natación", slug: "swimming", icon: "🏊" },
  ];

  return (
    <>
      <SEO
        title="Página No Encontrada - Error 404"
        description="La página que buscas no existe. Vuelve al directorio de gimnasios y equipamiento deportivo de Ekselencia."
        noindex={true}
      />
      <div className="flex min-h-screen items-center justify-center bg-background py-12 px-4">
        <div className="text-center max-w-4xl w-full">
          <h1 className="mb-4 text-6xl font-bold gradient-text">404</h1>
          <h2 className="mb-4 text-2xl font-semibold">Página No Encontrada</h2>
          <p className="mb-8 text-muted-foreground">
            Lo sentimos, la página que buscas no existe o ha sido movida.
          </p>

          {/* Suggested Matches */}
          {isSearching && (
            <div className="mb-8 text-muted-foreground">
              Buscando contenido similar...
            </div>
          )}

          {!isSearching && suggestions.length > 0 && (
            <div className="mb-12 max-w-2xl mx-auto">
              <h3 className="text-lg font-semibold mb-4">¿Buscabas esto?</h3>
              <div className="space-y-3">
                {suggestions.map((suggestion) => (
                  <Link key={suggestion.id} to={suggestion.path}>
                    <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="text-left">
                          <p className="font-medium">{suggestion.title}</p>
                          <p className="text-sm text-muted-foreground capitalize">{suggestion.type}</p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-primary" />
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="mb-12 max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold mb-4">¿Qué estás buscando?</h3>
            <SearchBar />
          </div>

          {/* Popular Categories */}
          <div className="mb-12">
            <div className="flex items-center justify-center gap-2 mb-6">
              <Grid className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-semibold">Categorías Populares</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {popularTypes.map((type) => (
                <Link key={type.slug} to={`/directory?type=${type.slug}`}>
                  <Card className="p-4 hover:shadow-lg transition-shadow cursor-pointer h-full">
                    <div className="text-center">
                      <div className="text-3xl mb-2">{type.icon}</div>
                      <p className="text-sm font-medium">{type.name}</p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Categories from Database */}
          {categories.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-center gap-2 mb-6">
                <Dumbbell className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Todas las Categorías</h3>
              </div>
              <div className="flex flex-wrap gap-2 justify-center max-w-3xl mx-auto">
                {categories.map((category) => (
                  <Link key={category.id} to={`/directory?category=${category.slug}`}>
                    <Button variant="outline" className="gap-2">
                      {category.icon && <span>{category.icon}</span>}
                      {category.name}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <Link to="/">
            <Button className="bg-gradient-primary">
              <Home className="w-4 h-4 mr-2" />
              Volver al Inicio
            </Button>
          </Link>
        </div>
      </div>
    </>
  );
};
export default NotFound;
